import React from 'react';
import { UI_CONSTANTS } from '../../../constants';

/**
 * Get file icon based on file type/category
 * WHY: Provides visual feedback for different file types
 */
const getFileIcon = (fileItem) => {
  const { category } = fileItem;

  // File type emoji mapping for visual feedback
  const iconMap = {
    image: '🖼️',
    document: '📄',
    spreadsheet: '📊',
    structured: '📋',
  };

  return iconMap[category] || '📎';
};

/**
 * Format file size for display
 * WHY: Shows human-readable file sizes
 */
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (bytes / Math.pow(k, i)).toFixed(2) + ' ' + sizes[i];
};

/**
 * Enhanced file preview component supporting multiple file types
 * 
 * CHANGES:
 * - Renamed from ImagePreview to FilePreview for accuracy
 * - Supports displaying multiple file types (images, documents, spreadsheets, data)
 * - Shows file type icons for non-image files
 * - Displays file metadata (size, type, extension)
 * - Image previews only for image files
 * - Document/data files show as cards with icon
 * - Improved error handling and fallbacks
 * 
 * @param {Array} files - Array of file objects from useFileUpload
 * @param {Function} onRemove - Callback to remove file by index
 * @param {string} error - Error message to display
 * @param {Function} onClearError - Callback to clear error
 * @param {number} totalSize - Total upload size
 * @param {number} maxTotalSize - Maximum allowed total size
 */
const FilePreview = ({ 
  files, 
  onRemove, 
  error, 
  onClearError,
  totalSize = 0,
  maxTotalSize = null,
}) => {
  // WHY: Check for files and error to prevent crashes from undefined states
  const hasFiles = files && Array.isArray(files) && files.length > 0;
  const hasError = error && typeof error === 'string';

  if (!hasFiles && !hasError) return null;

  // Calculate remaining space if maxTotalSize is provided
  const remainingSpace = maxTotalSize ? maxTotalSize - totalSize : null;
  const remainingPercent = remainingSpace && maxTotalSize ? (remainingSpace / maxTotalSize) * 100 : null;

  return (
    <div className="file-preview-container">
      {/* Display validation error messages to user */}
      {hasError && (
        <div className="file-error-message">
          <div className="error-content">
            <strong>⚠️ Upload Error:</strong>
            <p style={{ whiteSpace: 'pre-wrap', fontSize: '0.9em' }}>{error}</p>
            {onClearError && (
              <button
                onClick={onClearError}
                className="error-dismiss-btn"
                aria-label="Dismiss error"
                title="Dismiss error message"
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
      )}

      {/* Display upload progress bar if files are being uploaded */}
      {hasFiles && maxTotalSize && (
        <div className="upload-progress-container">
          <div className="progress-info">
            <span className="progress-label">
              {formatFileSize(totalSize)} / {formatFileSize(maxTotalSize)}
            </span>
            <span className="progress-percent">{remainingPercent?.toFixed(0)}% remaining</span>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${Math.max(0, Math.min(100, ((totalSize / maxTotalSize) * 100)))}%`,
                backgroundColor: totalSize > maxTotalSize * 0.9 ? '#ff6b6b' : '#4CAF50',
              }}
            />
          </div>
        </div>
      )}

      {/* Display file previews */}
      {hasFiles && (
        <div className="file-grid">
          {files.map((fileItem, index) => {
            const isImage = fileItem.category === 'image' && fileItem.preview;
            const fileIcon = getFileIcon(fileItem);
            const fileSize = formatFileSize(fileItem.size);

            return (
              <div
                key={`file-${index}-${fileItem.name}`}
                className={`file-preview ${fileItem.category}`}
                data-category={fileItem.category}
              >
                {/* Image preview for image files */}
                {isImage ? (
                  <img
                    src={fileItem.preview}
                    alt={`Preview: ${fileItem.name}`}
                    style={{
                      width: `${UI_CONSTANTS.IMAGE_PREVIEW_SIZE}px`,
                      height: `${UI_CONSTANTS.IMAGE_PREVIEW_SIZE}px`,
                      objectFit: 'cover',
                    }}
                    // WHY: Error handler prevents broken image layout
                    onError={(e) => {
                      console.error('Error loading image preview:', fileItem.name);
                      e.target.style.backgroundColor = '#f0f0f0';
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  // File type icon for non-image files
                  <div
                    className="file-icon-container"
                    style={{
                      width: `${UI_CONSTANTS.IMAGE_PREVIEW_SIZE}px`,
                      height: `${UI_CONSTANTS.IMAGE_PREVIEW_SIZE}px`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '2em',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '4px',
                    }}
                  >
                    {fileIcon}
                  </div>
                )}

                {/* File metadata overlay */}
                <div className="file-metadata">
                  <div className="file-name-truncated" title={fileItem.name}>
                    {fileItem.name.length > 20
                      ? `${fileItem.name.substring(0, 17)}...`
                      : fileItem.name}
                  </div>
                  <div className="file-info">
                    <span className="file-size">{fileSize}</span>
                    <span className="file-type">{fileItem.extension}</span>
                  </div>
                </div>

                {/* Remove button with error handling */}
                <button
                  onClick={() => {
                    try {
                      onRemove(index);
                    } catch (err) {
                      console.error('Error removing file:', err);
                    }
                  }}
                  className="remove-file-btn"
                  aria-label={`Remove ${fileItem.name}`}
                  title={`Remove ${fileItem.name}`}
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Backward compatibility: export as ImagePreview too
export default FilePreview;
export { FilePreview };
